import { useRef, useEffect, useState, useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist";
import workerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import { Button } from "@/components/ui/button";

pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;

interface PdfViewerProps {
  data: ArrayBuffer | Uint8Array | null;
  className?: string;
}

export function PdfViewer({ data, className }: PdfViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pdf, setPdf] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1.3);

  useEffect(() => {
    if (!data) return;
    let cancelled = false;
    const loadTask = pdfjsLib.getDocument({ data });
    loadTask.promise
      .then((doc) => {
        if (!cancelled) {
          setPdf(doc);
          setNumPages(doc.numPages);
          setPage(1);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [data]);

  const renderPage = useCallback(async () => {
    if (!pdf || !containerRef.current) return;
    try {
      const pg = await pdf.getPage(page);
      const viewport = pg.getViewport({ scale });
      const container = containerRef.current;

      let canvas = container.querySelector("canvas");
      if (!canvas) {
        canvas = document.createElement("canvas");
        container.innerHTML = "";
        container.appendChild(canvas);
      }

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const outputScale = window.devicePixelRatio || 1;
      canvas.width = Math.floor(viewport.width * outputScale);
      canvas.height = Math.floor(viewport.height * outputScale);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;

      ctx.setTransform(outputScale, 0, 0, outputScale, 0, 0);

      await pg.render({ canvasContext: ctx, viewport, canvas }).promise;
    } catch (err) {
      console.error("PDF render error:", err);
    }
  }, [pdf, page, scale]);

  useEffect(() => {
    renderPage();
  }, [renderPage]);

  return (
    <div className={`flex flex-col h-full ${className || ""}`}>
      {numPages > 0 && (
        <div className="flex items-center justify-center gap-2 py-2 px-4 bg-muted/30 border-b border-border/30 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-xs text-muted-foreground min-w-[80px] text-center">
            Page {page} of {numPages}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={page >= numPages}
            onClick={() => setPage((p) => Math.min(numPages, p + 1))}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          <div className="w-px h-4 bg-border mx-1" />
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setScale((s) => Math.max(0.5, s - 0.2))}
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-xs text-muted-foreground w-[40px] text-center">
            {Math.round(scale * 100)}%
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setScale((s) => Math.min(3, s + 0.2))}
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
        </div>
      )}
      <div
        ref={containerRef}
        className="flex-1 overflow-auto flex items-start justify-center bg-neutral-100 dark:bg-neutral-900 p-4"
      />
    </div>
  );
}
